import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { REREGISTER_REASONS, type ReregisterReason } from '@siva/shared';
import { post } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { errorMessage } from '../../lib/passkey.js';
import { Alert } from '../../lib/ui.js';
import { t } from '../../i18n/index.js';

export function ReregisterPage() {
  const { me } = useAuth();
  const signedIn = me?.session?.kind === 'worker';
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState<ReregisterReason>('new_phone');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/auth/reregister/request', {
        ...(signedIn ? {} : { phone }),
        reason,
        note: note || undefined,
      });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <section className="card">
        <Alert kind="success">{t('rereg_sent')}</Alert>
        {!signedIn && (
          <Link className="button secondary" to="/">
            {t('rereg_back')}
          </Link>
        )}
      </section>
    );
  }
  return (
    <section className="card">
      <h2>{t('rereg_title')}</h2>
      <p>{signedIn ? t('rereg_intro_signed_in') : t('rereg_intro')}</p>
      <form onSubmit={submit}>
        {!signedIn && (
          <label>
            {t('rereg_phone')}
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              autoComplete="tel"
            />
          </label>
        )}
        <label>
          {t('rereg_reason')}
          <select value={reason} onChange={(e) => setReason(e.target.value as ReregisterReason)}>
            {REREGISTER_REASONS.map((r) => (
              <option key={r} value={r}>
                {t(`rereg_reason_${r}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('rereg_note')}
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
        </label>
        {error && <Alert>{error}</Alert>}
        <button type="submit" disabled={busy}>
          {t('rereg_submit')}
        </button>
      </form>
      {/* Installed on a home screen there is no browser back button, so the way out has to be
          on the page. A signed-in worker already has the tab bar. */}
      {!signedIn && (
        <p>
          <Link className="link-block" to="/">
            {t('rereg_back')}
          </Link>
        </p>
      )}
    </section>
  );
}
